// User and Authentication Types
export interface User {
  id: string;
  username: string;
  email?: string; // Email is now optional
  pendingEmail?: string;
  emailVerified?: boolean;
  firstName: string;
  lastName: string;
  role: Role;
  companyId?: string;
  companyName?: string;
  company?: Company;
  priceEntryMode?: PriceEntryMode;
  defaultVatRate?: number;
  batchTrackingEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Company {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  priceEntryMode?: PriceEntryMode;
  defaultVatRate?: number;
  batchTrackingEnabled?: boolean;
  acquisitionSource?: AcquisitionSource;
  createdAt: string;
  updatedAt: string;
  userCount?: number;
  lastBusinessActivityAt?: string | null;
  usageStatus?: 'ACTIVE_30D' | 'INACTIVE_30D' | 'NEVER_USED';
  subscription?: {
    status: SubscriptionStatus;
    effectiveStatus: SubscriptionStatus;
    planCode: string;
    endsAt?: string | null;
    remainingDays?: number | null;
    readOnly: boolean;
  };
}

export enum AcquisitionSource {
  LEGACY_UNKNOWN = 'LEGACY_UNKNOWN',
  SELF_SIGNUP_ORGANIC = 'SELF_SIGNUP_ORGANIC',
  SELF_SIGNUP_REFERRAL = 'SELF_SIGNUP_REFERRAL',
  ADMIN_CREATED = 'ADMIN_CREATED',
}

export enum SubscriptionStatus {
  ACTIVE = 'ACTIVE',
  READ_ONLY = 'READ_ONLY',
  SUSPENDED = 'SUSPENDED',
}

export enum ItemType {
  PRODUCT = 'PRODUCT',
  SERVICE = 'SERVICE',
}

export enum InventoryMode {
  NONE = 'NONE',
  SIMPLE = 'SIMPLE',
  BATCHED = 'BATCHED',
}

export enum PriceEntryMode {
  VAT_INCLUDED = 'VAT_INCLUDED',
  VAT_EXCLUDED = 'VAT_EXCLUDED',
}

export interface SubscriptionStatusDto {
  status: SubscriptionStatus;
  effectiveStatus: SubscriptionStatus;
  planCode: string;
  startsAt?: string;
  endsAt?: string;
  readOnly: boolean;
  remainingDays?: number | null;
  notes?: string;
}

export interface CompanyAdminSummary {
  companyId: string;
  startDate: string;
  endDate: string;
  businessTimeZone: string;
  asOf: string;
  counts: { userCount: number; customerCount: number; itemCount: number };
  period: { completedSalesCount: number; totalSalesAmount: number; salesCollectionAmount: number };
  activity: { lastBusinessActivityAt?: string | null; usageStatus: 'ACTIVE_30D' | 'INACTIVE_30D' | 'NEVER_USED' };
}

export interface AdminOverview {
  startDate: string;
  endDate: string;
  activityWindowStart: string;
  activityWindowEnd: string;
  asOf: string;
  totalCompanyCount: number;
  activeSubscriptionCompanyCount: number;
  activeUsageCompanyCount: number;
  newCompanyCount: number;
  expiringIn7DaysCompanyCount: number;
  restrictedCompanyCount: number;
  businessTimeZone: string;
}

/** Read model returned by the superadmin company health/list endpoint. */
export interface AdminCompanyListItem extends Company {
  // The backend always sends the health fields for this endpoint. They stay
  // optional here so existing company list consumers remain source-compatible.
}

export interface AdminCompanyListPage extends PaginatedResponse<AdminCompanyListItem> {
  asOf: string;
}

export interface CompanyNote {
  id: string;
  companyId: string;
  createdBy: string;
  createdByUsername: string;
  note: string;
  createdAt: string;
}

export interface UpdateSubscriptionRequest {
  planCode: string;
  status: SubscriptionStatus;
  startsAt: string;
  endsAt?: string;
  notes?: string;
  paidActivation?: boolean;
}

export interface CompanyExportPreferences {
  productsStockEnabled: boolean;
  customerStatusEnabled: boolean;
  accountMovementsEnabled: boolean;
  salesSummaryEnabled: boolean;
  detailedSalesEnabled: boolean;
  maxRows: number;
}

export interface ExportPreview {
  estimatedRows: number;
  maxRows: number;
  allowed: boolean;
  message: string;
}

export interface CompanyExportDownload {
  blob: Blob;
  fileName: string;
}

export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  name?: string; // Full name provided by backend
  email?: string;
  phone?: string;
  address?: string;
  taxIdentificationNumber?: string;
  companyId: string;
  companyName?: string;
  company?: Company;
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: string;
  name: string;
  description?: string;
  color?: string;
  companyId: string;
  company?: Company;
  createdAt: string;
  updatedAt: string;
}

export interface Item {
  id: string;
  itemNumber: string;
  barcode?: string | null;
  name: string;
  description?: string;
  /** Canonical net price returned by the API's legacy price field. */
  price: number | null;
  /** Canonical VAT-inclusive catalog price. */
  priceGross?: number | null;
  vatRate?: number;
  priceEntryMode?: PriceEntryMode;
  unit?: string;
  companyId: string;
  companyName?: string;
  company?: Company;
  categories?: Category[];
  totalStockQuantity: number;
  enableBatchTracking: boolean;
  itemType?: ItemType;
  /** Preferred inventory contract; legacy API responses may omit it. */
  inventoryMode?: InventoryMode;
  createdAt: string;
  updatedAt: string;
}

export interface Debt {
  id: string;
  customerId: string;
  customerName: string;
  description?: string;
  amount: number;
  transactionDate: string;
  transactionDateTime?: string;
  debtType?: DebtType;
  saleId?: string;
  saleNumber?: string;
  createdAt: string;
  updatedAt: string;
  createdByUsername: string;
  companyName?: string;
}

export enum AccountMovementType {
  SALE = 'SALE',
  MANUAL_DEBT = 'MANUAL_DEBT',
  MANUAL_PAYMENT = 'MANUAL_PAYMENT',
  CANCELLATION = 'CANCELLATION',
}

/** Grouped account movement returned by the backend customer-account view. */
export interface AccountMovement {
  id: string;
  customerId: string;
  customerName: string;
  companyName?: string;
  movementType: AccountMovementType;
  description?: string;
  amount: number;
  grossAmount?: number;
  paidAmount?: number;
  remainingAmount?: number;
  paymentStatus?: PaymentStatus;
  saleStatus?: SaleStatus;
  saleId?: string;
  saleNumber?: string;
  transactionDate: string;
  transactionDateTime?: string;
  lastActivityDateTime?: string;
  sourceDebtId?: string;
  createdByUsername?: string;
  detailCount: number;
}

export interface CustomerBalance {
  customerId: string;
  customerName: string;
  amount: number;
}

export interface AccountBalanceSummary {
  totalReceivables: number;
  customersWithBalance: number;
  openSales: number;
  topDebtors: CustomerBalance[];
}

export interface DashboardChartPoint {
  date: string;
  sales: number;
  collection: number;
  expenses: number;
}

export interface PaymentPlanAttentionSummary {
  overdueCustomerCount: number;
  overdueInstallmentCount: number;
  overdueAmount: number;
  dueSoonCustomerCount: number;
  dueSoonInstallmentCount: number;
  dueSoonAmount: number;
}

export interface DashboardSummary {
  startDate: string;
  endDate: string;
  totalSales: number;
  totalCollection: number;
  totalExpenses: number;
  unpaidExpensesAmount: number;
  totalReceivables: number;
  stockValue: number;
  periodSalesCount: number;
  unpaidExpenseCount: number;
  zeroStockCount: number;
  customersWithBalance: number;
  openSales: number;
  expiredBatchCount: number;
  expiringBatchCount: number;
  topDebtors: CustomerBalance[];
  topSellingItems: TopSellingItem[];
  chart: DashboardChartPoint[];
  /** Optional while backend versions roll out independently. */
  paymentPlanAttention?: PaymentPlanAttentionSummary;
}
export interface Expense {
  id: string;
  description: string;
  category?: string;
  amount: number;
  expenseDate: string;
  paid: boolean;
  paymentMethod?: PaymentMethod;
  notes?: string;
  createdByUsername: string;
  createdAt: string;
  updatedAt: string;
}

export enum Role {
  SUPER_ADMIN = 'SUPER_ADMIN',
  COMPANY_ADMIN = 'COMPANY_ADMIN',
  EMPLOYEE = 'EMPLOYEE'
}

// Authentication Types
export interface LoginRequest {
  usernameOrEmail: string;
  password: string;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phoneNumber: string;
  password: string;
  companyName: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyWebsite?: string;
  referralCode?: string;
}

export interface ReferralCodeResponse {
  code: string | null;
  active: boolean;
  canCreate: boolean;
  shareUrl?: string | null;
}

export interface ReferralAcquisitionResponse {
  companyId: string;
  source: AcquisitionSource;
  referralId?: string;
  referralCodeId?: string;
  referralCode?: string;
  referrerUserId?: string;
  referrerName?: string;
  referrerUsername?: string;
  referredAt?: string;
}

export interface ReferralRewardResponse {
  id: string;
  referralId: string;
  milestone: string;
  status: 'PENDING' | 'APPLIED' | 'REVERSED';
  beneficiaryCompanyId?: string | null;
  reason?: string | null;
  createdAt: string;
  approvedAt?: string | null;
  appliedAt?: string | null;
  reversedAt?: string | null;
  extensionDays?: number | null;
  beforeEndsAt?: string | null;
  afterEndsAt?: string | null;
}

export interface ReverseReferralRewardRequest {
  reason: string;
}

export interface JwtResponse {
  /**
   * Access credentials are delivered through an HttpOnly cookie. The API
   * keeps this field for response compatibility but intentionally returns
   * null so the browser never receives a bearer token in JavaScript.
   */
  token: string | null;
  type: string;
  userId: string;
  username: string;
  email: string;
  role: Role;
  companyId?: string;
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (usernameOrEmail: string, password: string) => Promise<User>;
  logout: () => void;
  updateUser: (user: User) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  subscription?: SubscriptionStatusDto | null;
  isReadOnly?: boolean;
}

// API Response Types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  status: number;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

// Form Types
export interface CreateCompanyRequest {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  priceEntryMode?: PriceEntryMode;
  defaultVatRate?: number;
  batchTrackingEnabled?: boolean;
}

export interface UpdateCompanyRequest {
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  priceEntryMode?: PriceEntryMode;
  defaultVatRate?: number;
  batchTrackingEnabled?: boolean;
}

export interface CreateUserRequest {
  username: string;
  email?: string; // Email is now optional
  password: string;
  firstName: string;
  lastName: string;
  role: Role;
  companyId?: string;
}

export interface UpdateUserRequest {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  role?: Role;
}

export interface EmailChangeRequest {
  newEmail: string;
  currentPassword?: string;
}

export interface CreateCustomerRequest {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  address?: string;
  taxIdentificationNumber?: string;
}

export interface UpdateCustomerRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  taxIdentificationNumber?: string;
}

export interface CreateItemRequest {
  name: string;
  price?: number | null;
  vatRate?: number;
  itemNumber?: string;
  barcode?: string;
  categoryIds?: string[];
  initialStockQuantity?: number;
  enableBatchTracking?: boolean;
  itemType?: ItemType;
  inventoryMode?: InventoryMode;
  initialBatchNumber?: string;
  initialProductionDate?: string;
  initialExpirationDate?: string;
  initialBatchNotes?: string;
}

export interface UpdateItemRequest {
  name?: string;
  price?: number | null;
  vatRate?: number;
  itemNumber?: string;
  barcode?: string;
  categoryIds?: string[];
  enableBatchTracking?: boolean;
  inventoryMode?: InventoryMode;
}

export interface CreateCategoryRequest {
  name: string;
  description?: string;
  color?: string;
}

export interface UpdateCategoryRequest {
  name?: string;
  description?: string;
  color?: string;
}

export interface CreateDebtRequest {
  customerId: string;
  customerName?: string; // For creating new customers
  description?: string;
  amount: number;
  transactionDate?: string;
  transactionDateTime?: string;
}

export interface UpdateDebtRequest {
  customerId?: string;
  description?: string;
  amount?: number;
  transactionDate?: string;
  transactionDateTime?: string;
}

export interface CreateExpenseRequest {
  description: string;
  category?: string;
  amount: number;
  expenseDate: string;
  paid: boolean;
  paymentMethod?: PaymentMethod;
  notes?: string;
}

export interface UpdateExpenseRequest {
  description?: string;
  category?: string;
  amount?: number;
  expenseDate?: string;
  paid?: boolean;
  paymentMethod?: PaymentMethod;
  notes?: string;
}

// Component Props Types
export interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: Role[];
}

export interface SearchParams {
  name?: string;
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  minPrice?: number;
  maxPrice?: number;
  companyId?: string;
  categoryId?: string;
  effectiveStatus?: SubscriptionStatus;
  expiresWithinDays?: 7 | 30;
  usageStatus?: 'ACTIVE_30D' | 'INACTIVE_30D' | 'NEVER_USED';
  createdFrom?: string;
  createdTo?: string;
}

export interface DebtSearchParams {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  search?: string;
  createdBy?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  customerId?: string;
  companyId?: string;
  debtType?: DebtType;
}

export interface ExpenseSearchParams {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  search?: string;
  category?: string;
  paid?: boolean;
  startDate?: string;
  endDate?: string;
  companyId?: string;
}

export interface BulkItemUploadResponse {
  totalProcessed: number;
  successCount: number;
  errorCount: number;
  errors: string[];
  successfulItems: string[];
}

export interface BulkCustomerUploadResponse {
  totalProcessed: number;
  successCount: number;
  errorCount: number;
  errors: string[];
  successfulCustomers: string[];
}

// Inventory Management Types
export interface InventoryBatch {
  id: string;
  itemId: string;
  itemName: string;
  batchNumber?: string;
  quantity: number;
  remainingQuantity: number;
  price: number;
  priceGross?: number;
  vatRate?: number;
  priceEntryMode?: PriceEntryMode;
  productionDate?: string;
  expirationDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  expired: boolean;
  expiringSoon: boolean;
  empty: boolean;
}

export interface AddStockRequest {
  quantity: number;
  /** Sale price for a newly created tracked batch. Existing batches keep theirs. */
  unitPrice?: number;
  batchId?: string; // Add stock to an existing tracked batch
  batchNumber?: string;
  productionDate?: string;
  expirationDate?: string;
  notes?: string;
}

export interface RemoveStockRequest {
  quantity: number;
  notes?: string;
  batchId?: string; // For batch-tracked items
}

export interface AdjustStockRequest {
  newQuantity: number;
  /** Optional sale price change for the selected tracked batch. */
  newPrice?: number;
  notes?: string;
  batchId?: string; // For batch-tracked items
  batchNumber?: string; // Optional: for updating batch number
}

export interface InventoryOperationResponse {
  success: boolean;
  /** Stable backend error code; use this for localization instead of message text. */
  code?: string;
  message: string;
  itemId?: string;
  itemName?: string;
  previousQuantity?: number;
  newQuantity?: number;
  quantityChange?: number;
  affectedBatches?: InventoryBatch[];
}

export enum InventoryTransactionType {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
  ADJUST = 'ADJUST',
  SALE = 'SALE',
  MODE_CHANGE = 'MODE_CHANGE',
}

export interface InventoryTransaction {
  id: string;
  item?: Item;
  itemId?: string;
  itemName?: string;
  batchId?: string;
  batchNumber?: string;
  batch?: InventoryBatch;
  transactionType: InventoryTransactionType;
  quantityChange: number;
  quantityBefore: number;
  quantityAfter: number;
  unitPrice?: number;
  notes?: string;
  createdBy?: User;
  createdByUsername?: string;
  createdAt: string;
}

// Sales System Types
export enum PaymentStatus {
  UNPAID = 'UNPAID',
  PARTIAL = 'PARTIAL',
  PAID = 'PAID'
}

export enum SaleStatus {
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export enum PaymentMethod {
  CASH = 'CASH',
  CARD = 'CARD',
  BANK_TRANSFER = 'BANK_TRANSFER',
  CHECK = 'CHECK',
  OTHER = 'OTHER'
}

export enum DebtType {
  MANUAL = 'MANUAL',
  SALE = 'SALE'
}

export interface Sale {
  id: string;
  saleNumber: string;
  customerId: string;
  customerName: string;
  companyId: string;
  companyName: string;
  totalAmount: number;
  netAmount?: number;
  vatAmount?: number;
  paidAmount: number;
  remainingAmount: number;
  paymentStatus: PaymentStatus;
  saleStatus: SaleStatus;
  saleDate: string;
  notes?: string;
  createdById: string;
  createdByUsername: string;
  createdAt: string;
  updatedAt: string;
  saleItems?: SaleItem[];
  salePayments?: SalePayment[];
}

export interface SaleItem {
  id: string;
  saleId: string;
  itemId: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  unitNetPrice?: number;
  totalPrice: number;
  netAmount?: number;
  vatRate?: number;
  vatAmount?: number;
  batchId?: string;
  batchNumber?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SalePayment {
  id: string;
  saleId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  notes?: string;
  createdById: string;
  createdByUsername: string;
  createdAt: string;
}

export interface SalesStatistics {
  totalSales: number;
  totalPayments: number;
  outstandingAmount: number;
  completedSales: number;
  unpaidSales: number;
  partiallyPaidSales: number;
}

// Sales Request Types
export interface CreateSaleRequest {
  customerId: string;
  notes?: string;
}

export interface AddSaleItemRequest {
  itemId: string;
  quantity: number;
  unitPrice: number;
  batchId?: string;
}

export interface UpdateSaleItemQuantityRequest {
  quantity: number;
}

export interface CompleteSaleRequest {
  paymentAmount?: number;
  paymentMethod?: PaymentMethod;
  paymentNotes?: string;
}

export enum PaymentType {
  FULL_PAID = 'FULL_PAID',
  PARTIAL_PAID = 'PARTIAL_PAID',
  NO_PAYMENT = 'NO_PAYMENT'
}

export interface CompleteSaleWithPaymentRequest {
  paymentType: PaymentType;
  paymentAmount?: number;
  paymentMethod?: PaymentMethod;
  paymentNotes?: string;
}

export interface CreateAndCompleteSaleRequest {
  customerId: string;
  items: SaleItemRequest[];
  paymentType: PaymentType;
  paymentAmount?: number;
  paymentMethod?: PaymentMethod;
  paymentNotes?: string;
  saleNotes?: string;
  paymentPlan?: { installmentCount: number; firstDueDate: string };
}

export interface SaleItemRequest {
  itemId: string;
  quantity: number;
  unitPrice: number;
  batchId?: string;
  notes?: string;
}

export interface AddPaymentRequest {
  amount: number;
  paymentMethod: PaymentMethod;
  notes?: string;
}

// Shopping Cart Types (for frontend state management)
export interface CartItem {
  itemId: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  /** Price entered by the user; for services this is sent back to the API. */
  enteredUnitPrice: number;
  unitNetPrice: number;
  vatRate: number;
  netAmount: number;
  vatAmount: number;
  totalPrice: number;
  batchId?: string;
  batchNumber?: string;
  availableStock: number;
  enableBatchTracking: boolean;
  itemType?: ItemType;
  inventoryMode?: InventoryMode;
}

export interface ShoppingCart {
  customerId?: string;
  customerName?: string;
  items: CartItem[];
  totalAmount: number;
  notes?: string;
}

// Stock Validation Types
export interface StockValidationResult {
  isValid: boolean;
  availableQuantity: number;
  requestedQuantity: number;
  message?: string;
}

export interface BatchStockInfo {
  batchId: string;
  batchNumber: string;
  availableQuantity: number;
  unitPrice: number;
  unitPriceNet?: number;
  vatRate?: number;
  priceEntryMode?: PriceEntryMode;
  expirationDate?: string;
  productionDate?: string;
}

// Sales Search and Filter Types
export interface SalesSearchParams {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  customerId?: string;
  customerSearch?: string;
  saleStatus?: SaleStatus;
  paymentStatus?: PaymentStatus;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  saleNumber?: string;
  includeItems?: boolean;
  companyId?: string;
}

export interface TopSellingItem {
  itemId: string;
  itemName: string;
  quantity: number;
  amount: number;
}

// Enhanced Debt interface with sales relationship
export interface EnhancedDebt extends Debt {
  saleId?: string;
  debtType: DebtType;
}
